import { matchPath } from "react-router-dom";
import {
	V2ZomentumRoutes,
	ZomentumPageTitles,
	ZomentumRoutes
} from "@/Contracts";

export interface NamedEntity {
	readonly id?: string;
	readonly name?: string;
}

export interface DocumentEntity extends NamedEntity {
	readonly companyName?: string;
}

export interface PageTitleContext {
	readonly pathname: string;
	readonly opportunity?: NamedEntity | null;
	readonly document?: DocumentEntity | null;
	readonly template?: NamedEntity | null;
}

interface PageTitleRoute {
	readonly path: string;
	readonly label: string;
	readonly entity?: (
		context: PageTitleContext
	) => NamedEntity | null | undefined;
}

export const formatPageTitle = (label: string, name?: string): string =>
	name
		? `${name} - ${label} | ${ZomentumPageTitles.Zomentum}`
		: `${label} | ${ZomentumPageTitles.Zomentum}`;

const PAGE_TITLE_ROUTES: readonly PageTitleRoute[] = [
	{ path: ZomentumRoutes.OpportunityList, label: "Opportunities" },
	{
		path: ZomentumRoutes.OpportunityDetail,
		label: "Opportunity",
		entity: context => context.opportunity
	},
	{ path: ZomentumRoutes.DocumentsList, label: "Documents" },
	{ path: ZomentumRoutes.DocumentsTemplatesList, label: "Templates" },
	{ path: ZomentumRoutes.DocumentsSavedLayoutsList, label: "Saved Layouts" },
	{ path: ZomentumRoutes.SavedBlockEditor, label: "Saved Block" },
	{
		path: ZomentumRoutes.DocumentsDetail,
		label: "Editor",
		entity: context => context.document
	},
	{
		path: ZomentumRoutes.TemplatesDetail,
		label: "Template",
		entity: context => context.template
	},
	{ path: ZomentumRoutes.ProductsList, label: "Products" },
	{ path: ZomentumRoutes.ProductsDetail, label: "Product" },
	{ path: ZomentumRoutes.ClientsList, label: "Clients" },
	{ path: ZomentumRoutes.ClientsDetail, label: "Client" },
	{ path: ZomentumRoutes.ContactsList, label: "Contacts" },
	{
		path: ZomentumRoutes.SalesActivityAutomationsList,
		label: "Sales Automation"
	},
	{
		path: ZomentumRoutes.SalesActivityAutomationCreate,
		label: "Sales Automation"
	},
	{
		path: ZomentumRoutes.SalesActivityAutomationsEdit,
		label: "Sales Automation"
	},
	{
		path: ZomentumRoutes.SalesActivityAutomationsClone,
		label: "Sales Automation"
	},
	{ path: ZomentumRoutes.SalesActivityPage, label: "Sales Activity" },
	{ path: ZomentumRoutes.ReportsList, label: "Reports" },
	{ path: ZomentumRoutes.ReportsDetail, label: "Report" },
	{ path: ZomentumRoutes.CreateReport, label: "Create Report" },
	{ path: ZomentumRoutes.AuditLogs, label: "Audit Logs" },
	{ path: ZomentumRoutes.McpAgents, label: "MCP Agents" },
	{ path: ZomentumRoutes.McpAgentDetail, label: "MCP Agent" },
	{ path: ZomentumRoutes.BulkActionResultPage, label: "Bulk Action" },
	{ path: ZomentumRoutes.Settings, label: "Settings" },
	{ path: ZomentumRoutes.AccountSettings, label: "Account Settings" },
	{ path: ZomentumRoutes.TimezoneSettings, label: "Account Settings" },
	{ path: ZomentumRoutes.UserSettings, label: "Users" },
	{ path: ZomentumRoutes.PipelineSettings, label: "Pipelines" },
	{ path: ZomentumRoutes.EmailSettings, label: "Email Settings" },
	{ path: ZomentumRoutes.EmailTemplateSettings, label: "Email Templates" },
	{ path: ZomentumRoutes.CustomFieldsSettings, label: "Custom Fields" },
	{ path: ZomentumRoutes.CustomFieldsList, label: "Custom Fields" },
	{ path: ZomentumRoutes.TaxRegionSettings, label: "Tax Regions" },
	{ path: ZomentumRoutes.InvoicesList, label: "Invoices" },
	{ path: ZomentumRoutes.CancelSubscription, label: "Cancel Subscription" },
	{ path: ZomentumRoutes.Notifications, label: "Notifications" },
	{ path: ZomentumRoutes.Billing, label: "Billing" },
	{ path: ZomentumRoutes.Suppliers, label: "Suppliers" },
	{ path: ZomentumRoutes.Preferences, label: "Preferences" },
	{ path: ZomentumRoutes.DocumentApproval, label: "Document Approval" },
	{
		path: ZomentumRoutes.DocumentApprovalCreatePage,
		label: "Document Approval"
	},
	{
		path: ZomentumRoutes.DocumentApprovalDetailsPage,
		label: "Document Approval"
	},
	{ path: ZomentumRoutes.IntegrationSettings, label: "Integrations" },
	{
		path: ZomentumRoutes.QuickbooksIntegration,
		label: "QuickBooks Integration"
	},
	{ path: ZomentumRoutes.AutoTaskIntegration, label: "Autotask Integration" },
	{
		path: ZomentumRoutes.ConnectWiseIntegration,
		label: "ConnectWise Integration"
	},
	{ path: ZomentumRoutes.SyncroIntegration, label: "Syncro Integration" },
	{
		path: ZomentumRoutes.RepairShoprIntegrationPage,
		label: "RepairShopr Integration"
	},
	{ path: ZomentumRoutes.HubSpotIntegration, label: "HubSpot Integration" },
	{ path: ZomentumRoutes.InfusionSoftIntegration, label: "Keap Integration" },
	{ path: ZomentumRoutes.AirtableIntegration, label: "Airtable Integration" },
	{
		path: ZomentumRoutes.MsTeamsIntegration,
		label: "Microsoft Teams Integration"
	},
	{ path: ZomentumRoutes.KaseyaIntegration, label: "Kaseya Integration" },
	{ path: V2ZomentumRoutes.KASEYA, label: "Kaseya Integration" },
	{ path: ZomentumRoutes.ZapierIntegrationPage, label: "Zapier Integration" },
	{
		path: ZomentumRoutes.HaloPSAIntegrationPage,
		label: "HaloPSA Integration"
	},
	{
		path: ZomentumRoutes.CustomDistributorIntegrationAdd,
		label: "Distributor Integration"
	},
	{
		path: ZomentumRoutes.CustomDistributorIntegrationDetail,
		label: "Distributor Integration"
	},
	{ path: ZomentumRoutes.LoginOrSignup, label: "Welcome" },
	{ path: ZomentumRoutes.Login, label: "Log In" },
	{ path: ZomentumRoutes.VerifyEmail, label: "Verify Email" },
	{ path: ZomentumRoutes.SetupAccount, label: "Set Up Account" }
];

const nameIfCurrent = (
	entity: NamedEntity | null | undefined,
	routeId: string | undefined
): string | undefined =>
	entity?.id && entity.id === routeId && entity.name
		? entity.name
		: undefined;

const resolvePreviewTitle = (
	context: PageTitleContext,
	documentId: string
): string => {
	const sendingDocument =
		context.document?.id === documentId ? context.document : undefined;
	const titleParts = [
		sendingDocument?.name,
		sendingDocument?.companyName
	].filter(Boolean);
	return titleParts.length
		? titleParts.join(" | ")
		: ZomentumPageTitles.Zomentum;
};

export const resolvePageTitle = (context: PageTitleContext): string => {
	const previewMatch = matchPath<{ id: string }>(context.pathname, {
		path: ZomentumRoutes.DocumentsPreview,
		exact: true
	});
	if (previewMatch) {
		return resolvePreviewTitle(context, previewMatch.params.id);
	}

	for (const route of PAGE_TITLE_ROUTES) {
		const match = matchPath<{ id?: string }>(context.pathname, {
			path: route.path,
			exact: true
		});
		if (match) {
			const entityName = route.entity
				? nameIfCurrent(route.entity(context), match.params.id)
				: undefined;
			return formatPageTitle(route.label, entityName);
		}
	}

	return ZomentumPageTitles.Zomentum;
};
